"use client";

import type { CSSProperties } from "react";

/**
 * The served brand's accent, as inline CSS-var overrides for the app shell.
 *
 * WHY THIS EXISTS (parity sweep, 2026-08-25). Both apps ship the SAME
 * static VIP palette — `--c-accent: #44689d` in either stylesheet — and
 * the reference nonetheless renders its brand accent on every page,
 * because its layouts apply the served profile's accent at runtime
 * (`experienceThemeStyle(user.experience_profile)` on the layout root,
 * reference/scopely-frontend/src/app/{dashboard,partner}/layout.tsx). This
 * app only did that inside the wizard shell, so every other page fell
 * back to the static palette and a branded surface rendered unbranded.
 *
 * THE INPUT IS THE SERVED SURFACE'S BRANDING, and that is the correct
 * source here: the accent answers "which application am I looking at",
 * which is exactly what the surface decides — unlike the work-item noun,
 * which belongs to the ORG's own profile (see order-wizard's
 * `workItemNoun` note; conflating those two is a bug this repo has already
 * paid for). The default surface serves no branding at all, so the
 * unbranded ZoomWarriors surface keeps its own palette untouched.
 *
 * Derivation mirrors the backend's own (`_vip_experience_profile` in
 * apps/spine_api/views.py): every value comes from `accent` alone —
 * the hover and CTA values equal it (no separate hover state exists), and
 * the soft tint is the same rgba(accent, 0.14) the wire carries.
 */
export type BrandThemeStyle = CSSProperties & Record<`--${string}`, string>;

/** #rgb / #rrggbb only — anything else is not a colour we will inject. */
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function softTint(hex: string): string {
  const full =
    hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
  const r = parseInt(full.slice(1, 3), 16);
  const g = parseInt(full.slice(3, 5), 16);
  const b = parseInt(full.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.14)`;
}

export function brandThemeStyle(
  branding: Readonly<Record<string, string>> | null | undefined,
): BrandThemeStyle | undefined {
  const accent = (branding?.accent ?? "").trim();
  // No branding, or a value we cannot vouch for, leaves the palette alone.
  // This is load-bearing rather than defensive: the wire legitimately
  // serves "" for a profile with no accent configured, and pushing an
  // empty string into a custom property blanks every colour derived from
  // it instead of falling back.
  if (!HEX.test(accent)) return undefined;
  return {
    "--c-accent": accent,
    "--c-accent-hover": accent,
    "--c-accent-cta": accent,
    "--c-accent-cta-hover": accent,
    "--c-blue-pale": softTint(accent),
  };
}
